import React, { useEffect, useMemo, useState } from 'react';
import { Package, RefreshCw } from 'lucide-react';

export default function WooProductCustomerSegments() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const refresh = async () => {
    try {
      setLoading(true);
      const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
      if (!brandId) { setRows([]); return; }
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const url = params.toString()
        ? `${base}/api/woocommerce/products/customer-repeat?${params.toString()}`
        : `${base}/api/woocommerce/products/customer-repeat`;
      const res = await fetch(url, { credentials: 'include', headers });
      const js = await res.json().catch(() => ({}));
      const data = res.ok && js?.rows ? js.rows : [];
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const products = useMemo(() => {
    return (rows || []).map(r => ({
      productId: r.product_id || null,
      sku: r.sku,
      name: r.product_name || 'Unknown',
      ge1: Number(r.customers_ge1 || 0),
      ge2: Number(r.customers_ge2 || 0),
      ge3: Number(r.customers_ge3 || 0),
      ge4: Number(r.customers_ge4 || 0),
    }));
  }, [rows]);

  const periodText = useMemo(() => {
    if (startDate && endDate) return `${startDate} → ${endDate}`;
    if (startDate && !endDate) return `${startDate} → (today)`;
    if (!startDate && endDate) return `(all time) → ${endDate}`;
    return 'All time';
  }, [startDate, endDate]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
      {loading ? (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex items-center gap-3 text-gray-700">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/woocommercelogo.webp" alt="WooCommerce" className="h-8 object-contain" />
            <h1 className="text-xl font-bold text-gray-900">Product Customer Segments</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-2 py-1 text-sm border rounded-lg"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-2 py-1 text-sm border rounded-lg"
              />
              <button
                className={`px-3 py-1.5 text-sm rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors ${loading ? 'opacity-50' : ''}`}
                onClick={refresh}
                disabled={loading}
              >
                Apply
              </button>
            </div>
            <button
              className={`p-2 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors ${loading ? 'opacity-50' : ''}`}
              onClick={refresh}
              title="Refresh"
              disabled={loading}
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Period info */}
      <div className="text-sm text-gray-600">
        Period: <span className="font-medium text-gray-800">{periodText}</span>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        {products.length === 0 ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-gray-700">
                  <th className="px-3 py-2 text-left font-medium">Product</th>
                  <th className="px-3 py-2 text-left font-medium">SKU</th>
                  <th className="px-3 py-2 text-right font-medium">≥1</th>
                  <th className="px-3 py-2 text-right font-medium">≥2</th>
                  <th className="px-3 py-2 text-right font-medium">≥3</th>
                  <th className="px-3 py-2 text-right font-medium">≥4</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, idx) => (
                  <tr key={`${p.productId || 'n'}-${p.sku || idx}`} className="border-b last:border-b-0">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Package size={16} className="text-indigo-600" />
                        <span className="font-medium text-gray-900">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{p.sku || '-'}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{p.ge1.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{p.ge2.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{p.ge3.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{p.ge4.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


